import { useState } from "react";

// ────────────────────────────────────────────────────────────────────────
// ChargeSpark — Privacy Policy
// Same visual system/pattern as the DBLearn / PDF Studio policy pages
// (dark "legal document" layout, Space Grotesk display type), palette
// pulled from ChargeSpark's own in-app theme (lib/utils/app_colors.dart —
// deep navy/purple background, blue → magenta brand gradient) and content
// written for what ChargeSpark actually does: a lock-screen charging
// animation overlay, on-device theme/settings storage, no account, no
// user file uploads, and Google Mobile Ads (AdMob) for monetization with
// GDPR/UMP consent + a rewarded-ad trial-time system.
//
// BEFORE DEPLOYING:
//   1. Confirm CONTACT_EMAIL / DEVELOPER_NAME are correct.
//   2. Re-read every section against what the app actually does. If you
//      add accounts, cloud sync, analytics, or a backend later, this page
//      must be updated to stay accurate — a privacy policy that doesn't
//      match real behavior is a compliance risk, not just a copy problem.
//   3. If you ever mark the app as "Designed for Families" / child-
//      directed in Play Console, the "Children's privacy" and "Ads"
//      sections below need to be rewritten — and the "Hot" theme category
//      (romantic/suggestive animations) would need to be removed or
//      gated, since it isn't appropriate for a child-directed listing.
//   4. Swap the AdMob test ad unit IDs in ad_manager.dart for your real
//      ones before release — this policy assumes the real AdMob SDK is
//      live and serving ads.
// ────────────────────────────────────────────────────────────────────────

const CONTACT_EMAIL = "alphay28071994@gmail.com"; // TODO: replace with your real support email
const DEVELOPER_NAME = "AlphaY"; // TODO: replace with your name/company
const APP_NAME = "ChargeSpark";

// Pulled from ChargeSpark's in-app theme (lib/utils/app_colors.dart) so
// the policy page reads as part of the same product, not a generic
// template.
const palette = {
  bg: "#05071A",
  surface: "#0E0E1C",
  raised: "#141428",
  border: "#242244",
  accent: "#22D3FF",
  accentDim: "#0B2430",
  magenta: "#FF2FD1",
  magentaDim: "#2B0B24",
  text: "#FFFFFF",
  muted: "rgba(255,255,255,0.6)",
  faint: "rgba(255,255,255,0.4)",
  success: "#22E37A",
  successDim: "#0B2A18",
  danger: "#FF5A5F",
  dangerDim: "#2B0F10",
  warning: "#FFA726",
  warningDim: "#2B1F0A",
  info: "#22D3FF",
  infoDim: "#0B2430",
  gold: "#FFC24B",
  goldDim: "#2B1F0A",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  .cs-pp {
    background: ${palette.bg};
    color: ${palette.text};
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  .cs-bars-bg {
    position: fixed;
    inset: 0;
    background-image: repeating-linear-gradient(
      90deg,
      ${palette.border}33 0px,
      ${palette.border}33 2px,
      transparent 2px,
      transparent 60px
    );
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }

  .cs-glow {
    position: fixed;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 640px;
    height: 420px;
    background: radial-gradient(ellipse, ${palette.accent}22 0%, ${palette.magenta}14 45%, transparent 70%);
    pointer-events: none;
    z-index: 0;
    animation: cs-pulse 5s ease-in-out infinite alternate;
  }

  @keyframes cs-pulse {
    from { opacity: 0.6; }
    to   { opacity: 1; }
  }

  .cs-wrap {
    position: relative;
    z-index: 1;
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 80px;
  }

  .cs-hero {
    text-align: center;
    padding: 56px 0 44px;
    animation: cs-fadeup .6s ease both;
  }

  .cs-mark {
    width: 52px;
    height: 52px;
    margin: 0 auto 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, ${palette.accent}, ${palette.magenta});
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .cs-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}44;
    border-radius: 100px;
    padding: 5px 14px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    color: ${palette.accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .cs-hero h1 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(30px, 6vw, 48px);
    font-weight: 700;
    letter-spacing: -1px;
    line-height: 1.1;
    margin-bottom: 12px;
  }

  .cs-hero h1 em {
    font-style: normal;
    background: linear-gradient(90deg, ${palette.accent}, ${palette.magenta});
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .cs-hero-sub {
    color: ${palette.muted};
    font-size: 14px;
    max-width: 460px;
    margin: 0 auto 18px;
  }

  .cs-meta {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px 20px;
    color: ${palette.faint};
    font-size: 12px;
    font-family: 'JetBrains Mono', monospace;
  }

  .cs-meta span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .cs-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${palette.border}, transparent);
    margin: 36px 0;
  }

  .cs-section {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 24px 26px 20px;
    margin-bottom: 12px;
    transition: border-color .2s;
    animation: cs-fadeup .5s ease both;
  }

  .cs-section:hover { border-color: ${palette.accent}30; }

  .cs-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .cs-icon {
    width: 36px; height: 36px; min-width: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .cs-section h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -.1px;
  }

  .cs-section p, .cs-section li {
    color: ${palette.muted};
    font-size: 14px;
    line-height: 1.75;
  }

  .cs-section ul {
    padding-left: 18px;
    margin-top: 8px;
  }

  .cs-section li { margin-bottom: 5px; }

  .cs-section p + p { margin-top: 10px; }

  .cs-section a {
    color: ${palette.info};
    text-decoration: none;
    border-bottom: 1px solid ${palette.info}44;
    transition: border-color .15s;
  }

  .cs-section a:hover { border-color: ${palette.info}; }

  .cs-box {
    border-radius: 10px;
    padding: 11px 15px;
    margin-top: 14px;
    font-size: 13px;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.6;
  }

  .cs-box-success {
    background: ${palette.successDim};
    border: 1px solid ${palette.success}33;
    color: ${palette.success}CC;
  }

  .cs-box-warning {
    background: ${palette.warningDim};
    border: 1px solid ${palette.warning}33;
    color: ${palette.warning}CC;
  }

  .cs-box-danger {
    background: ${palette.dangerDim};
    border: 1px solid ${palette.danger}33;
    color: ${palette.danger}CC;
  }

  .cs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 14px 0 4px;
  }

  @media (max-width: 520px) { .cs-grid { grid-template-columns: 1fr; } }

  .cs-card {
    background: ${palette.raised};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 13px 15px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .cs-card-icon {
    width: 32px; height: 32px; min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .cs-card strong {
    font-size: 13px;
    font-weight: 600;
    color: ${palette.text};
    display: block;
    margin-bottom: 3px;
  }

  .cs-card p {
    font-size: 12px;
    color: ${palette.muted};
    line-height: 1.5;
    margin: 0 !important;
  }

  .cs-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 14px;
    font-size: 13px;
  }

  .cs-table th, .cs-table td {
    text-align: left;
    padding: 9px 10px;
    border-bottom: 1px solid ${palette.border};
    vertical-align: top;
  }

  .cs-table th {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px;
    letter-spacing: .5px;
    text-transform: uppercase;
    color: ${palette.faint};
    font-weight: 600;
  }

  .cs-table td { color: ${palette.muted}; }
  .cs-table td:first-child { color: ${palette.text}; font-weight: 600; white-space: nowrap; }

  .cs-contact {
    background: ${palette.accentDim};
    border: 1px solid ${palette.accent}33;
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    margin-top: 36px;
  }

  .cs-contact h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .cs-contact p {
    color: ${palette.muted};
    font-size: 14px;
    margin-bottom: 20px;
  }

  .cs-contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(90deg, ${palette.accent}, ${palette.magenta});
    color: #05071A;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 12px 24px;
    border-radius: 100px;
    text-decoration: none;
    transition: opacity .2s, transform .2s;
  }

  .cs-contact-btn:hover { opacity: .87; transform: translateY(-1px); }

  .cs-footer {
    text-align: center;
    padding-top: 28px;
    color: ${palette.faint};
    font-size: 12px;
  }

  @keyframes cs-fadeup {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const Ico = ({ e }) => <span role="img" aria-hidden="true">{e}</span>;

function SecIcon({ bg, children }) {
  return <div className="cs-icon" style={{ background: bg }}>{children}</div>;
}

function Section({ iconBg, icon, title, children, delay = 0 }) {
  return (
    <div className="cs-section" style={{ animationDelay: `${delay}s` }}>
      <div className="cs-section-head">
        <SecIcon bg={iconBg}><Ico e={icon} /></SecIcon>
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SuccessBox({ children }) {
  return <div className="cs-box cs-box-success"><Ico e="✓" /><span>{children}</span></div>;
}

function WarnBox({ children }) {
  return <div className="cs-box cs-box-warning"><Ico e="⚠" /><span>{children}</span></div>;
}

function InfoCard({ icon, iconBg, title, desc }) {
  return (
    <div className="cs-card">
      <div className="cs-card-icon" style={{ background: iconBg }}>
        <Ico e={icon} />
      </div>
      <div>
        <strong>{title}</strong>
        <p>{desc}</p>
      </div>
    </div>
  );
}

export default function ChargeSparkPrivacyPolicy() {
  const [year] = useState(new Date().getFullYear());

  return (
    <>
      <style>{css}</style>
      <div className="cs-pp">
        <div className="cs-bars-bg" />
        <div className="cs-glow" />

        <div className="cs-wrap">

          <header className="cs-hero">
            <div className="cs-mark">⚡</div>
            <div className="cs-badge">🔐 &nbsp;Legal document</div>
            <h1>Privacy <em>Policy</em></h1>
            <p className="cs-hero-sub">
              How <strong>{APP_NAME}</strong> collects, uses, and protects your information.
            </p>
            <div className="cs-meta">
              <span>🏢 Developer: {DEVELOPER_NAME}</span>
              <span>📅 Effective: January {year}</span>
              <span>📱 No account · No file uploads</span>
            </div>
          </header>

          <div className="cs-divider" />

          <Section iconBg={palette.accentDim} icon="🔋" title="Overview" delay={0.05}>
            <p>
              <strong style={{ color: palette.text }}>{APP_NAME}</strong> is a battery charging-animation
              customization app for Android. It replaces the plain lock-screen charging indicator with an
              animated overlay (Lottie or native Canvas animations) of your choosing, along with a live
              battery percentage, whenever your device starts charging.
            </p>
            <p>
              This page explains what information (if any) {APP_NAME} collects, how the app uses device
              permissions to make the overlay work, and how advertising is handled. By using {APP_NAME},
              you agree to the practices described below.
            </p>
            <SuccessBox>
              {APP_NAME} requires no account or sign-in, and does not collect your name, email, or any
              other personal identifying information.
            </SuccessBox>
          </Section>

          <Section iconBg={palette.infoDim} icon="🗂️" title="Information we collect" delay={0.10}>
            <p>
              <strong style={{ color: palette.text }}>We do not operate any servers that this app sends your
              data to.</strong> There is no account system and no crash-reporting or analytics service built
              by us into this app.
            </p>
            <p>
              Everything {APP_NAME} remembers about your setup — the animation theme you selected, its
              on-screen position, your app settings, and your remaining free trial time — is stored
              locally on your device (via Android SharedPreferences), inside the app's own private
              storage. None of it is transmitted to us or to any server we control.
            </p>
            <p>
              To show the correct battery percentage on the overlay, {APP_NAME} reads your device's
              current battery level and charging state directly from the Android system. This reading is
              used only to render the animation in real time and is never stored, logged, or sent
              anywhere.
            </p>
            <SuccessBox>
              Uninstalling the app, or clearing its storage in your device settings, permanently deletes
              all locally saved theme selections, settings, and trial-time data.
            </SuccessBox>
          </Section>

          <Section iconBg={palette.warningDim} icon="📢" title="Advertising" delay={0.15}>
            <p>
              {APP_NAME} is free to use and supported by ads served through{" "}
              <strong style={{ color: palette.text }}>Google Mobile Ads (AdMob)</strong>. The app shows a
              home-screen banner ad, an app-open ad on cold start, a rewarded ad when you choose to unlock
              extra free animation time or apply certain themes, and an occasional interstitial ad after
              you close a theme preview without applying it.
            </p>
            <p>
              To serve ads, Google's SDK may collect device and advertising identifiers (such as your
              Android advertising ID), approximate location derived from IP address, and other technical
              data, and may use these to personalize the ads you see. This processing is performed by
              Google, not by {APP_NAME} directly — we don't receive or store this data ourselves.
            </p>
            <p>
              Where required (for example, in the EEA, UK, and Switzerland), {APP_NAME} shows a consent
              form — built on Google's User Messaging Platform (UMP) — before any personalized ads are
              requested, so you can choose how your data is used for advertising, or opt out of
              personalization entirely.
            </p>
            <p>
              Watching a rewarded ad is always optional: declining or closing it never blocks you from
              using the app's core theme-browsing features, and a missing ad fill is treated the same as
              a decline.
            </p>
            <WarnBox>
              Google's ad and consent handling is governed by Google's own privacy policy —{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                policies.google.com/privacy
              </a>{" "}
              and{" "}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer">
                policies.google.com/technologies/ads
              </a>
            </WarnBox>
          </Section>

          <Section iconBg={palette.successDim} icon="🔑" title="Permissions used" delay={0.20}>
            <p>
              {APP_NAME} requests the following Android permissions, all of them used solely to make the
              charging overlay work — none are used to collect personal data:
            </p>
            <table className="cs-table">
              <thead>
                <tr><th>Permission</th><th>Why it's needed</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Display over other apps</td>
                  <td>Draws the animated overlay on top of the lock screen when charging starts. This is the core mechanism the app is built around.</td>
                </tr>
                <tr>
                  <td>Foreground service</td>
                  <td>Runs a lightweight, always-visible-notification service that watches your device's charging state so the overlay can appear immediately when you plug in.</td>
                </tr>
                <tr>
                  <td>Run at device startup</td>
                  <td>Restarts the charging-state monitor automatically after you reboot your device, so the overlay keeps working without having to reopen the app.</td>
                </tr>
                <tr>
                  <td>Notifications</td>
                  <td>Required by Android to keep the foreground service running; shows a persistent low-priority notification while the monitor is active.</td>
                </tr>
                <tr>
                  <td>Full-screen intent</td>
                  <td>Lets the overlay open as a full-screen activity over the lock screen instead of a small heads-up notification.</td>
                </tr>
                <tr>
                  <td>Wake lock</td>
                  <td>Keeps the screen briefly awake so the animation is visible right when you plug in, rather than staying dark.</td>
                </tr>
                <tr>
                  <td>Ignore battery optimizations (optional)</td>
                  <td>An optional exemption you can grant so aggressive OEM battery managers don't kill the monitoring service in the background. The app works without it, just less reliably on some devices.</td>
                </tr>
                <tr>
                  <td>Internet &amp; advertising ID</td>
                  <td>Used only to request, load, and display ads through Google Mobile Ads (see Advertising above).</td>
                </tr>
              </tbody>
            </table>
            <p>
              {APP_NAME} does not request camera, microphone, contacts, SMS, call log, storage, or precise
              location permissions, and does not read or write files outside of its own app storage.
            </p>
          </Section>

          <Section iconBg="#0A1525" icon="🔌" title="Third-party components" delay={0.25}>
            <p>{APP_NAME} is built using the following components:</p>
            <div className="cs-grid">
              <InfoCard
                icon="📢" iconBg={palette.warningDim}
                title="Google Mobile Ads (AdMob)"
                desc="Serves banner, app-open, rewarded, and interstitial ads; see Advertising above."
              />
              <InfoCard
                icon="✅" iconBg={palette.infoDim}
                title="Google User Messaging Platform"
                desc="Collects your ad-consent choice where required by law."
              />
              <InfoCard
                icon="🎞️" iconBg={palette.accentDim}
                title="Lottie animations"
                desc="Plays bundled animation files locally; no data is sent anywhere."
              />
              <InfoCard
                icon="🗄️" iconBg={palette.successDim}
                title="Android SharedPreferences"
                desc="Stores your selected theme, settings, and trial time on-device."
              />
              <InfoCard
                icon="🔋" iconBg={palette.goldDim}
                title="Android Battery Manager"
                desc="Reads live battery %/charge state to render the overlay in real time."
              />
              <InfoCard
                icon="⚙️" iconBg="#1A100A"
                title="Foreground service"
                desc="Watches charging state in the background to trigger the overlay."
              />
            </div>
          </Section>

          <Section iconBg="#0A1525" icon="🕓" title="Data retention" delay={0.30}>
            <p>
              Your selected theme, app settings, and trial-time balance remain in the app's local storage
              until you change them yourself, reset the app's data, or uninstall it — at which point the
              operating system removes them automatically.
            </p>
            <p>We do not maintain any server-side copy of this data.</p>
          </Section>

          <Section iconBg={palette.dangerDim} icon="🧒" title="Children's privacy" delay={0.35}>
            <p>
              {APP_NAME} is a general-audience personalization app and is not directed at children under
              13. Some optional animation themes (grouped under the "Hot" category) feature romantic or
              suggestive imagery and are intended for a general adult/teen audience, not young children.
            </p>
            <p>
              We do not knowingly collect personal information from children. Ads shown in the app are
              served under Google's standard (non-child-directed) treatment.
            </p>
            <p>
              If you believe a child has used the app in a way that concerns you, please contact us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </Section>

          <Section iconBg={palette.successDim} icon="🛡️" title="Security" delay={0.40}>
            <p>
              Your theme selections, settings, and trial-time balance live in {APP_NAME}'s private,
              sandboxed storage on your device, inaccessible to other apps.
            </p>
            <p>
              Since nothing is transmitted to us, the biggest safeguard is your own device security — keep
              it locked with a passcode or biometric lock, and keep the app updated to receive any
              security fixes.
            </p>
          </Section>

          <Section iconBg={palette.warningDim} icon="🔄" title="Changes to this policy" delay={0.45}>
            <p>
              We may update this privacy policy from time to time. Changes will be posted on this page
              with an updated effective date at the top.
            </p>
            <p>
              Continued use of {APP_NAME} after changes are posted constitutes acceptance of the updated
              policy.
            </p>
          </Section>

          <Section iconBg={palette.successDim} icon="✅" title="Your consent" delay={0.50}>
            <p>
              By using {APP_NAME}, you consent to this privacy policy. If you do not agree, please
              discontinue use of the app.
            </p>
          </Section>

          <div className="cs-contact">
            <h3>Questions or concerns?</h3>
            <p>
              If you have any questions about how {APP_NAME} handles your data, don't hesitate to get in
              touch.
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="cs-contact-btn">
              ✉️ &nbsp;{CONTACT_EMAIL}
            </a>
          </div>

          <div className="cs-divider" />
          <footer className="cs-footer">
            <p>© {year} <strong style={{ color: palette.muted }}>{DEVELOPER_NAME}</strong> · {APP_NAME} · All rights reserved.</p>
            <p style={{ marginTop: 5 }}>This app is not affiliated with or endorsed by Google LLC.</p>
          </footer>

        </div>
      </div>
    </>
  );
}